import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthStore } from './stores/auth.store';
import { SocketProvider } from './contexts/SocketContext';
import { AuthProvider } from './contexts/AuthContext';
import LoginPage from './pages/auth/LoginPage';
import DashboardLayout from './layouts/DashboardLayout';
import DashboardPage from './pages/dashboard/DashboardPage';
import SchedulingCanvas from './pages/schedules/SchedulingCanvas';
import SchedulesPageWrapper from './pages/schedules/SchedulesPageWrapper';
import ModernForecastsPage from './pages/forecasts/ModernForecastsPage';
import ForecastsPageWrapper from './pages/forecasts/ForecastsPageWrapper';
import ShiftPlanningPage from './pages/forecasts/ShiftPlanningPage';
import AgentsPage from './pages/agents/AgentsPage';
import TimePunchesPage from './pages/time-punches/TimePunchesPage';
import AdminTimePunchesPage from './pages/time-punches/AdminTimePunchesPage';
import AgentTimePunchesPage from './pages/time-punches/AgentTimePunchesPage';
import RealtimePage from './pages/realtime/RealtimePage';
import SettingsPage from './pages/settings/SettingsPage';
import Billing from './pages/Billing';
import TimeOffPage from './pages/time-off/TimeOffPage';
import AdminReviewPage from './pages/time-off/AdminReviewPage';
import ReportingPage from './pages/reporting/ReportingPage';
import SavingsReportPage from './pages/reports/SavingsReportPage';
import DayInReviewPage from './pages/day-in-review/DayInReviewPage';

// Platform Admin imports
import PlatformAdminLoginPage from './pages/platform-admin/auth/PlatformAdminLoginPage';
import PlatformAdminLayout from './layouts/PlatformAdminLayout';
import PlatformDashboard from './pages/platform-admin/dashboard/PlatformDashboard';
import PlatformOrganizationsPage from './pages/platform-admin/organizations/PlatformOrganizationsPage';
import OrganizationDetailPage from './pages/platform-admin/organizations/OrganizationDetailPage';
import SystemEventsPage from './pages/platform-admin/events/SystemEventsPage';
import EventDetailPage from './pages/platform-admin/events/EventDetailPage';
import UsageAnalyticsPage from './pages/platform-admin/usage/UsageAnalyticsPage';
import FeatureFlagsPage from './pages/platform-admin/feature-flags/FeatureFlagsPage';
import BatchOperationsPage from './pages/platform-admin/batch/BatchOperationsPage';
import AuditLogsPage from './pages/platform-admin/audit/AuditLogsPage';
import AdminUsersPage from './pages/platform-admin/admin-users/AdminUsersPage';
import ProfilePage from './pages/platform-admin/profile/ProfilePage';
import { PlatformAdminProtectedRoute } from './components/platform-admin/PlatformAdminProtectedRoute';

function RoleBasedHome() {
  const user = useAuthStore((state) => state.user);

  if (user?.role === 'AGENT') {
    return <Navigate to="/time-punches/agent" replace />;
  }

  return <DashboardPage />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [isHydrated, setIsHydrated] = useState(false);
  const token = useAuthStore((state) => state.token);

  useEffect(() => {
    // Wait for next tick to ensure store has hydrated
    const unsubscribe = useAuthStore.persist.onFinishHydration(() => {
      setIsHydrated(true);
    });

    // Check if already hydrated
    useAuthStore.persist.hasHydrated() && setIsHydrated(true);

    return unsubscribe;
  }, []);

  // Wait for store to hydrate before making routing decisions
  if (!isHydrated) {
    return null;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function RoleProtectedRoute({
  children,
  allowedRoles
}: {
  children: React.ReactNode;
  allowedRoles: string[];
}) {
  const user = useAuthStore((state) => state.user);

  if (!user || !allowedRoles.includes(user.role)) {
    // Redirect to appropriate page based on role
    if (user?.role === 'AGENT') {
      return <Navigate to="/time-punches/agent" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            {/* Platform Admin Routes */}
            <Route path="/platform-admin/login" element={<PlatformAdminLoginPage />} />
            <Route
              path="/platform-admin"
              element={
                <PlatformAdminProtectedRoute>
                  <PlatformAdminLayout />
                </PlatformAdminProtectedRoute>
              }
            >
              <Route index element={<PlatformDashboard />} />
              <Route path="organizations" element={<PlatformOrganizationsPage />} />
              <Route path="organizations/:id" element={<OrganizationDetailPage />} />
              <Route path="usage" element={<UsageAnalyticsPage />} />
              <Route path="events" element={<SystemEventsPage />} />
              <Route path="events/:id" element={<EventDetailPage />} />
              <Route path="feature-flags" element={<FeatureFlagsPage />} />
              <Route path="batch" element={<BatchOperationsPage />} />
              <Route path="admin-users" element={<AdminUsersPage />} />
              <Route path="audit" element={<AuditLogsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<RoleBasedHome />} />
            <Route
              path="schedules"
              element={
                <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'SUPERVISOR']}>
                  <SchedulesPageWrapper />
                </RoleProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/schedules/canvas" replace />} />
              <Route path="canvas" element={<SchedulingCanvas />} />
              <Route path="requests" element={<AdminReviewPage />} />
            </Route>
            <Route
              path="forecasts"
              element={
                <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'SUPERVISOR']}>
                  <ForecastsPageWrapper />
                </RoleProtectedRoute>
              }
            >
              <Route index element={<ModernForecastsPage />} />
              <Route path="shift-planning" element={<ShiftPlanningPage />} />
            </Route>
            <Route
              path="agents"
              element={
                <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'SUPERVISOR']}>
                  <AgentsPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="reporting"
              element={
                <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'SUPERVISOR']}>
                  <ReportingPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="reports/savings"
              element={
                <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'SUPERVISOR']}>
                  <SavingsReportPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="reporting/day-in-review"
              element={
                <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'SUPERVISOR']}>
                  <DayInReviewPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="time-punches"
              element={
                <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'SUPERVISOR']}>
                  <TimePunchesPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="time-punches/admin"
              element={
                <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'SUPERVISOR']}>
                  <AdminTimePunchesPage />
                </RoleProtectedRoute>
              }
            />
            <Route path="time-punches/agent" element={<AgentTimePunchesPage />} />
            <Route path="time-off" element={<TimeOffPage />} />
            <Route
              path="realtime"
              element={
                <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'SUPERVISOR']}>
                  <RealtimePage />
                </RoleProtectedRoute>
              }
            />
            <Route path="settings" element={<SettingsPage />} />
            <Route
              path="billing"
              element={
                <RoleProtectedRoute allowedRoles={['ADMIN']}>
                  <Billing />
                </RoleProtectedRoute>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;
